<template>
    <SeatSelectionCard
        :seats="seats"
        :loading="loadingAsientos"
        :error="errorBloquear || errorAsientos"
        :selected-seats="asientosSeleccionados"
        :max-seats="maxAsientos"
        :zone-name="zonaSeleccionada?.name ?? ''"
        :sidebar-title="$t('perfil_invitaciones.butacas_seleccionadas')"
        :limit-label="$t('perfil_invitaciones.limite_asientos_alcanzado', { max: maxAsientos })"
        :empty-label="$t('perfil_invitaciones.sin_butacas_seleccionadas')"
        :total-label="$t('perfil_invitaciones.total_seleccionadas')"
        :row-label="$t('perfil_invitaciones.fila')"
        :seat-label="$t('perfil_invitaciones.butaca')"
        :map-hint-label="$t('perfil_invitaciones.selecciona_butacas')"
        :ticket-label="index => $t('perfil_invitaciones.invitacion_n', { n: index + 1 })"
        :tipo-recinto="asientosData?.tipo_recinto ?? null"
        @toggle-asiento="$emit('toggle-asiento', $event)"
        @quitar-asiento="$emit('quitar-asiento', $event)"
    />
</template>

<script setup>
    import { computed } from 'vue';
    import SeatSelectionCard from '@/modules/front/ticketing/SeatSelectionCard.vue';

    const props = defineProps({
        asientosData: {
            type: Object,
            default: null,
        },
        loadingAsientos: {
            type: Boolean,
            default: false,
        },
        errorAsientos: {
            type: [String, Boolean],
            default: null,
        },
        errorBloquear: {
            type: [String, Boolean],
            default: null,
        },
        asientosSeleccionados: {
            type: Array,
            default: () => [],
        },
        maxAsientos: {
            type: Number,
            default: 0,
        },
        zonaSeleccionada: {
            type: Object,
            default: null,
        },
    });

    defineEmits(['toggle-asiento', 'quitar-asiento']);

    /**
     * Mapea los datos crudos del backend al shape esperado por SeatPicker.
     *
     * El endpoint devuelve `configuracion_asientos` como un objeto de filas:
     *   { "A": [{ asiento, fila, ocupado, id?, ... }], "B": [...], ... }
     *
     * Aplana ese objeto en un array plano normalizado para SeatPicker.
     * Disponibilidad: `ocupado === 0` es libre; 1 = ocupado, 3 = bloqueado.
     */
    const seats = computed(() => {
        const conf = props.asientosData?.configuracion_asientos;
        if (!conf || typeof conf !== 'object') return [];

        const result = [];
        const filas = Array.isArray(conf) ? conf : Object.entries(conf);

        if (Array.isArray(conf)) {
            for (const fila of conf) {
                const filaAlias = fila.alias ?? fila.fila ?? '';
                const asientos = Array.isArray(fila.asientos)
                    ? fila.asientos
                    : Object.values(fila.asientos ?? {});
                for (const a of asientos) {
                    result.push(_normalizeAsiento(a, filaAlias));
                }
            }
        } else {
            for (const [filaKey, asientos] of filas) {
                const lista = Array.isArray(asientos) ? asientos : Object.values(asientos ?? {});
                for (const a of lista) {
                    result.push(_normalizeAsiento(a, filaKey));
                }
            }
        }

        return _deduplicarPorId(result);
    });

    /**
     * Elimina asientos duplicados por ID (defensivo: el backend a veces devuelve
     * la misma butaca dos veces con distinto estado). Conserva la primera ocurrencia.
     */
    function _deduplicarPorId(seats) {
        const seen = new Set();
        const deduped = [];
        for (const seat of seats) {
            if (seen.has(seat.id)) continue;
            seen.add(seat.id);
            deduped.push(seat);
        }
        return deduped;
    }

    /** Normaliza un objeto asiento crudo al shape de SeatPicker */
    function _normalizeAsiento(a, filaKey) {
        const fila = a.fila ?? filaKey ?? '';
        const numero = a.asiento ?? a.numero ?? a.number ?? '';
        const id = a.id ?? `${fila}-${numero}`;
        const available = a.disponible ?? a.available ?? (a.ocupado === 0 || a.ocupado === false);
        return {
            id,
            row: fila,
            number: numero,
            available,
            label: a.label ?? null,
            sectionName: a.sector ?? a.sectionName ?? null,
        };
    }
</script>
